"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { generateTemplateDetailsPath } from "./slug";
import { useOnScreen } from "@/helpers/hooks/use-on-screen";
import { apiClient } from "@/server/init";
import { TemplateFilter } from "@/shared/templates/constants";
import { TemplateModal } from "@/shared/templates/template-modal";
import { TemplateTile } from "@/shared/templates/template-tile";
import { fetchTemplates, TemplateData } from "@/shared/templates/templates-action";

interface TemplateMasonryProps {
  filter: TemplateFilter | undefined;
  searchQuery?: string;
  initialData?: TemplateData[];

  /**
   * determines if we update the URL when a model is opened. This is only used on the public templates page so a user can share a link to a specific template
   */
  updateUrlOnModelOpen?: boolean;
}

export function TemplateMasonry({
  filter,
  searchQuery,
  initialData,
  updateUrlOnModelOpen = false,
}: TemplateMasonryProps) {
  const limit = 20;

  // we store this copy to detect if search query changed, if so, we don't want to show the initialData
  // without this check, there is a flash of initialData shown while still fetching
  const [initialQuery] = useState(searchQuery);

  const projectsQuery = useInfiniteQuery({
    queryKey: ["templates", { filter, searchQuery }],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.templates.getMany({
        query: {
          offset: pageParam,
          limit: limit,
          filter: filter,
          searchQuery: searchQuery,
        },
      });

      return response.body.items;
    },
    ...(initialData && initialQuery === searchQuery
      ? { initialData: { pages: [initialData], pageParams: [0] } }
      : {}),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      // the recommendation training data includes all templates used even if the template is disabled
      // this mean we could return less than the limit even if there are more. So just checking if we have any results in the last page
      let check = filter === "for-you" ? 1 : limit;

      if (lastPage.length < check) return;

      return pages.flat().length;
    },
    staleTime: 1,
    enabled: limit > 0,
  });

  const templates = projectsQuery.data?.pages.flat() ?? [];

  const [idx, setIdx] = useState<number | undefined>(undefined);

  const handleIdx = (idx: number) => {
    if (idx >= templates.length) {
      setIdx(0);
    } else if (idx < 0) {
      setIdx(templates.length - 1);
    } else {
      setIdx(idx);
    }
  };

  let [isOpen, setIsOpen] = useState(false);

  const pathname = usePathname();
  const router = useRouter();
  const [originalPathname] = useState<string | null>(pathname);

  useEffect(() => {
    if (!updateUrlOnModelOpen) return;

    if (!pathname) return;

    if (pathname.includes("details") && !isOpen) {
      router.push(pathname);
    } else if (!pathname.includes("details")) {
      setIsOpen(false);
    }
  }, [pathname]);

  function closeModal() {
    setIsOpen(false);
    setIdx(undefined);

    if (updateUrlOnModelOpen) {
      history.pushState({}, "", originalPathname);
    }
  }

  const openModal = useCallback((idx: number) => {
    setIdx(idx);
    setIsOpen(true);

    if (updateUrlOnModelOpen) {
      const template = templates[idx];

      if (!template) return;

      history.pushState({}, "", `/${generateTemplateDetailsPath(template)}`);
    }
  }, []);

  const { setRef: loadMoreRef, isVisible: shouldLoadMore } = useOnScreen(0.5);
  useEffect(() => {
    if (shouldLoadMore && projectsQuery.hasNextPage) {
      projectsQuery.fetchNextPage();
    }
  }, [shouldLoadMore]);

  const renderMasonry = () => {
    if (projectsQuery.isPending) {
      return (
        <Masonry gutter="40px">
          <Skeleton height={200} />
          <Skeleton height={240} />
          <Skeleton height={160} />
          <Skeleton height={120} />
          <Skeleton height={160} />
          <Skeleton height={200} />
          <Skeleton height={240} />
          <Skeleton height={160} />
          <Skeleton height={120} />
          <Skeleton height={160} />
        </Masonry>
      );
    }

    return (
      <Masonry gutter="40px">
        {templates.map((project, idx) => (
          <TemplateTile key={project.id} {...project} idx={idx} onClick={openModal} />
        ))}
      </Masonry>
    );
  };

  return (
    <div className="px-2 pb-8 sm:px-0">
      <ResponsiveMasonry
        columnsCountBreakPoints={{
          300: 2,
          600: 2,
          900: 3,
          1200: 4,
          1500: 5,
          1800: 6,
          2100: 7,
          2400: 8,
        }}
      >
        {renderMasonry()}
      </ResponsiveMasonry>
      <div ref={loadMoreRef} className="h-2"></div>

      <TemplateModal
        template={idx === undefined ? undefined : templates[idx]}
        idx={idx}
        isOpen={isOpen}
        closeModal={closeModal}
        handleIdx={handleIdx}
      />
    </div>
  );
}

function Skeleton({ height }: { height: number }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="skeleton h-32 w-full" style={{ height: height }}></div>
      <div className="skeleton h-4 w-28"></div>
    </div>
  );
}
